import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { useAtomValue, useSetAtom } from "jotai";

import {
  AssetType,
  sideBarWidthAtom,
  modalOriginPositionAtom,
  focusModeAssetAtom,
  previewNodeIdAtom,
  togglesAtomResettable,
} from "@zapier/canvas-state";
import {
  PRODUCT_NAV_WIDTH,
  FOCUS_MODE_CONTAINER_RADIUS,
  FOCUS_MODE_TIMING,
} from "@zapier/canvas-constants";
import { Alert, Button, Spacer, Spinner, Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";

import { getButtonNameByTheAssetType } from "../../utils/getButtonNameByTheAssetType";
import { getIconNameByAssetType } from "../../utils/getIconNameByAssetType";
import { useEmitUserInteractionEvents } from "../../hooks/useEmitUserInteractionEvents";
import { useAssetNavigation } from "../../hooks/useAssetNavigation";
import { usePreloadedIframe } from "../../hooks/usePreloadedIframe";
import { CanvasCopilotIndicator } from "../../components/CanvasCopilotIndicator";

import styles from "./FocusModeView.module.css";
import RefreshAssetButton from "../RefreshAssetButton";

interface FocusModeViewProps {
  assetType: AssetType;
  title: string;
  url: string | undefined;
  assetId: string | null;
  onClose: () => void;
}

export function FocusModeView({
  title,
  url,
  assetType,
  assetId,
  onClose,
}: FocusModeViewProps) {
  const { emitEventOnAssetOpen } = useEmitUserInteractionEvents();
  const sideBarWidth = useAtomValue(sideBarWidthAtom);
  const modalOriginPosition = useAtomValue(modalOriginPositionAtom);
  const setModalOriginPosition = useSetAtom(modalOriginPositionAtom);
  const setFocusModeAsset = useSetAtom(focusModeAssetAtom);
  const previewNodeId = useAtomValue(previewNodeIdAtom);
  const [isClosing, setIsClosing] = useState(false);
  const toggles = useAtomValue(togglesAtomResettable);

  const { isPreloaded, showLoader } = usePreloadedIframe(
    url,
    sideBarWidth,
    isClosing,
  );

  useEffect(() => {
    if (assetType) {
      setFocusModeAsset({ type: assetType, id: assetId });
    }
  }, [assetType, assetId, setFocusModeAsset]);

  const transformOrigin = modalOriginPosition
    ? `${modalOriginPosition.x}px ${modalOriginPosition.y}px`
    : "center center";

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      // Clean up origin position before closing
      setModalOriginPosition(null);
      onClose();
    }, FOCUS_MODE_TIMING.CLOSE_DELAY);
  }, [onClose, setModalOriginPosition]);

  const navigation = useAssetNavigation({
    url,
    assetType,
    onAssetOpen: emitEventOnAssetOpen,
  });

  const checkForErrorContent = useCallback((iframe: HTMLIFrameElement) => {
    try {
      if (!iframe || !iframe.contentDocument) {
        return false;
      }

      // Access iframe content (works because it's same-origin)
      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc && iframeDoc.body) {
        const bodyText = iframeDoc.body.textContent || "";

        if (
          bodyText.includes("Unable to access Zap") ||
          bodyText.includes("You don't have access") ||
          bodyText.includes("Permission denied") ||
          bodyText.includes("Access forbidden") ||
          bodyText.includes("no longer exists")
        ) {
          // Error content detected, no need for loader management
          return true;
        }
      }
    } catch (error) {
      // Defensive error handling for cross-origin or permission issues
      console.error("Could not access iframe content:", error);
    }
    return false;
  }, []);

  const handleIframeLoad = useCallback(
    (event: React.SyntheticEvent<HTMLIFrameElement>, assetType: AssetType) => {
      // Skip if we're using preloaded iframe (it's already loaded)
      if (isPreloaded) {
        return;
      }

      const iframe = event.currentTarget;

      if (!iframe) {
        return;
      }

      if (url) {
        // Check for error content for zaps
        // This code is temporary and should go away soon
        // Slack thread: https://zapier.slack.com/archives/C07KUG0V89K/p1752859099987649
        if (assetType === "zap") {
          const hasError = checkForErrorContent(iframe);
          if (hasError) {
            return;
          }
        }
      }
    },
    [url, isPreloaded, checkForErrorContent],
  );

  const getBorderRadius = (width: number) =>
    width > PRODUCT_NAV_WIDTH ? "0px" : `${FOCUS_MODE_CONTAINER_RADIUS}px`;

  const assetTypeCapitalized = assetType
    ? assetType.charAt(0).toUpperCase() + assetType.slice(1)
    : "";

  return (
    <div
      className={classNames(styles.overlay, {
        [styles.overlayClosing]: isClosing,
      })}
      style={{
        left: `${sideBarWidth}px`,
        borderTopLeftRadius: getBorderRadius(sideBarWidth),
        transformOrigin,
      }}
    >
      <div
        className={styles.container}
        data-testid="focus-mode-container"
        style={{
          borderTopLeftRadius: getBorderRadius(PRODUCT_NAV_WIDTH),
        }}
      >
        <div>
          <div
            className={styles.header}
            style={{
              borderTopLeftRadius: getBorderRadius(PRODUCT_NAV_WIDTH),
            }}
          >
            <div className={styles.leftSection}>
              <Button
                variant="tertiary"
                size="small"
                iconPosition="start"
                onPress={handleClose}
                className={styles.backButton}
              >
                <Icon name="arrowBigLeft" />
                Back to Canvas
              </Button>

              <Spacer width={8} />
              <Icon
                name={getIconNameByAssetType(assetType)}
                size={16}
                color="BrandOrange"
              />
              <Spacer width={4} />
              <Text type="Body1" fontWeight={400} className={styles.titleText}>
                {title}
              </Text>
              <Spacer width={8} />
            </div>

            <div className={styles.rightSection}>
              <RefreshAssetButton isLoading={showLoader} />
              <Button
                variant="primary"
                size="small"
                onPress={navigation.handleGoToAsset}
                isDisabled={!navigation.canNavigate}
              >
                {getButtonNameByTheAssetType(assetType)}
              </Button>
            </div>
          </div>
          {!showLoader && (
            <Alert variant="banner" status="info">
              <Text type="Body2" color="TextDefault">
                This preview is read-only
                {toggles.aiCopilotCobuild
                  ? ", but Copilot can help you modify it."
                  : "."}{" "}
                For full editing capabilities, go to the {assetTypeCapitalized}{" "}
                builder.
              </Text>
            </Alert>
          )}
        </div>

        {showLoader && url && (
          <div className={styles.loaderContainer}>
            <div className={styles.loader}>
              <Spinner size="small" />
              <Spacer height={8} />
              <Text type="Body1" color="TextWeakest">
                Loading preview...
              </Text>
            </div>
          </div>
        )}

        <CanvasCopilotIndicator
          inFocusMode
          nodeId={previewNodeId || ""}
          isClosing={isClosing}
        />

        {url && !isPreloaded && (
          <iframe
            title={title}
            src={url}
            className={classNames(styles.iframe, {
              [styles.iframeHidden]: showLoader,
            })}
            sandbox={"allow-same-origin allow-scripts"}
            onLoad={(event) => handleIframeLoad(event, assetType)}
          />
        )}
      </div>
    </div>
  );
}
